import React from "react";
import { XCircle, Clock, User, Edit, Trash2, RotateCcw } from "lucide-react";
import { format } from "date-fns";

const formatDate = (dateString) => {
  if (!dateString) return "-";
  return format(new Date(dateString), "MMM dd, yyyy hh:mm a");
};

// ✅ Config and builder live HERE — parent knows nothing about icons/colors
const AUDIT_CONFIG = {
  "Created By": {
    icon: <User size={14} className="text-slate-500" />,
    color: "bg-slate-100",
  },
  "Last Edited By": {
    icon: <Edit size={14} className="text-indigo-500" />,
    color: "bg-indigo-50",
  },
  "Last Restored By": {
    icon: <RotateCcw size={14} className="text-green-500" />,
    color: "bg-green-50",
  },
  "Deleted By": {
    icon: <Trash2 size={14} className="text-red-500" />,
    color: "bg-red-50",
  },
};

export const buildAuditRows = (item) =>
  [
    {
      label: "Created By",
      name: item?.createdByFullName || "System",
      date: item?.createdDate,
    },
    {
      label: "Last Edited By",
      name: item?.updatedByFullName,
      date: item?.updatedDate,
    },
    {
      label: "Last Restored By",
      name: item?.restoredByFullName,
      date: item?.restoredDate,
    },
    {
      label: "Deleted By",
      name: item?.deletedByFullName,
      date: item?.deletedDate,
    },
  ].map((row) => ({ ...row, ...AUDIT_CONFIG[row.label] }));

// ✅ Modal receives only: isOpen, onClose, title, rows
const AuditInfoModal = ({ isOpen, onClose, title, rows }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-slate-100 flex justify-between items-center">
          <div>
            <h2 className="text-base font-bold text-slate-800">Audit Info</h2>
            {title && (
              <p className="text-xs text-slate-400 mt-0.5">
                Record:{" "}
                <span className="font-semibold text-indigo-600">{title}</span>
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition"
          >
            <XCircle size={20} />
          </button>
        </div>

        {/* Rows */}
        <div className="p-4 flex flex-col gap-3">
          {rows && rows.filter((r) => r.name).length > 0 ? (
            rows
              .filter((row) => row.name)
              .map((row, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100"
                >
                  <div
                    className={`p-2 rounded-lg ${row.color || "bg-slate-100"}`}
                  >
                    {row.icon}
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                      {row.label}
                    </span>
                    <span className="text-sm font-bold text-slate-800">
                      {row.name}
                    </span>
                    <span className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                      <Clock size={10} /> {formatDate(row.date)}
                    </span>
                  </div>
                </div>
              ))
          ) : (
            <p className="text-center text-xs text-slate-400 py-4">
              No audit history available.
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 pb-4">
          <button
            onClick={onClose}
            className="w-full py-2 text-sm font-medium bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuditInfoModal;
